/**
 * v4.12.0 — Lightweight configurator modals that collect the payload
 * for a bulk op, then hand control to <BulkPreviewModal />.
 *
 * Kept intentionally minimal — these are intermediate steps and should
 * feel like quick choices, not full editors.
 */
import { useEffect, useState } from "react";
import { sortByName } from '../../../lib/sortByName';
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import api from "../../../lib/api";

const Shell = ({ title, onClose, testId, children }) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
             data-testid={testId}>
            <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
            <div
                className="relative w-full max-w-md rounded-2xl border"
                style={{
                    background: "rgba(8, 14, 28, 0.97)",
                    borderColor: "rgba(56, 189, 248, 0.30)",
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)",
                }}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b"
                     style={{ borderColor: "rgba(148, 163, 184, 0.12)" }}>
                    <h3 className="text-base font-semibold text-slate-100"
                        style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="configurator-close"
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
                    ><X size={16} /></button>
                </div>
                <div className="px-5 py-5">{children}</div>
            </div>
        </div>
    );
};

const PrimaryButton = ({ onClick, disabled, children, testId }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-slate-900 bg-sky-400 hover:bg-sky-300 disabled:bg-slate-600 disabled:text-slate-400 transition-colors"
    >
        {children}
    </button>
);

const ChoiceRow = ({ value, label, selected, onSelect, testId }) => (
    <button
        type="button"
        onClick={() => onSelect(value)}
        data-testid={testId}
        className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
            selected
                ? "border-sky-300/60 bg-sky-400/10 text-slate-100"
                : "border-slate-500/20 text-slate-300 hover:border-sky-300/40 hover:bg-white/5"
        }`}
    >
        {label}
    </button>
);

// ───────────────────── status ─────────────────────
export function StatusConfigurator({ open, onClose, onChoose }) {
    const { t } = useTranslation();
    const [v, setV] = useState("active");
    if (!open) return null;
    return (
        <Shell title={t("card_fleet.bulk.select_status_title")} onClose={onClose} testId="bulk-config-status">
            <div className="space-y-2">
                <ChoiceRow value="active" label={t("card_fleet.bulk.select_status_active")} selected={v === "active"} onSelect={setV} testId="status-choice-active" />
                <ChoiceRow value="paused" label={t("card_fleet.bulk.select_status_paused")} selected={v === "paused"} onSelect={setV} testId="status-choice-paused" />
                <ChoiceRow value="inactive" label={t("card_fleet.bulk.select_status_inactive")} selected={v === "inactive"} onSelect={setV} testId="status-choice-inactive" />
            </div>
            <div className="mt-5">
                <PrimaryButton onClick={() => onChoose({ status: v })} testId="status-review-changes">
                    {t("card_fleet.bulk.review_changes")}
                </PrimaryButton>
            </div>
        </Shell>
    );
}

// ───────────────────── mode ─────────────────────
export function ModeConfigurator({ open, onClose, onChoose }) {
    const { t } = useTranslation();
    const [v, setV] = useState("standard");
    if (!open) return null;
    return (
        <Shell title={t("card_fleet.bulk.select_mode_title")} onClose={onClose} testId="bulk-config-mode">
            <div className="space-y-2">
                <ChoiceRow value="standard" label={t("card_fleet.bulk.select_mode_standard")} selected={v === "standard"} onSelect={setV} testId="mode-choice-standard" />
                <ChoiceRow value="review_shield" label={t("card_fleet.bulk.select_mode_review_shield")} selected={v === "review_shield"} onSelect={setV} testId="mode-choice-review-shield" />
                <ChoiceRow value="direct" label={t("card_fleet.bulk.select_mode_direct")} selected={v === "direct"} onSelect={setV} testId="mode-choice-direct" />
            </div>
            <div className="mt-5">
                <PrimaryButton onClick={() => onChoose({ mode: v })} testId="mode-review-changes">
                    {t("card_fleet.bulk.review_changes")}
                </PrimaryButton>
            </div>
        </Shell>
    );
}

// ───────────────────── platform / routing ─────────────────────
const PLATFORMS = ["google_reviews", "facebook", "instagram", "trustpilot", "yelp", "tripadvisor"];

export function PlatformConfigurator({ open, onClose, onChoose }) {
    const { t } = useTranslation();
    const [v, setV] = useState("google_reviews");
    if (!open) return null;
    return (
        <Shell title={t("card_fleet.bulk.select_platform_title")} onClose={onClose} testId="bulk-config-platform">
            <div className="grid grid-cols-2 gap-2">
                {PLATFORMS.map((p) => (
                    <ChoiceRow
                        key={p}
                        value={p}
                        label={t(`card_fleet.platforms.${p}`, { defaultValue: p })}
                        selected={v === p}
                        onSelect={setV}
                        testId={`platform-choice-${p}`}
                    />
                ))}
            </div>
            <div className="mt-5">
                <PrimaryButton onClick={() => onChoose({ platform: v })} testId="platform-review-changes">
                    {t("card_fleet.bulk.review_changes")}
                </PrimaryButton>
            </div>
        </Shell>
    );
}

// ───────────────────── assignment ─────────────────────
export function AssignmentConfigurator({ open, onClose, onChoose, locationFilter }) {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState([]);
    const [pick, setPick] = useState(""); // "" = unassign

    useEffect(() => {
        if (!open) return;
        api.get("/employees", { params: locationFilter ? { location_id: locationFilter } : {} })
            .then((r) => {
                const list = Array.isArray(r?.data) ? r.data : (r?.data?.employees || []);
                setEmployees(list.filter((e) => e?.is_active !== false));
            })
            .catch(() => setEmployees(sortByName([])));
    }, [open, locationFilter]);

    if (!open) return null;
    return (
        <Shell title={t("card_fleet.bulk.select_assignment_title")} onClose={onClose} testId="bulk-config-assignment">
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                <ChoiceRow
                    value=""
                    label={t("card_fleet.bulk.assignment_unassign")}
                    selected={pick === ""}
                    onSelect={setPick}
                    testId="assign-choice-unassign"
                />
                {employees.length === 0 ? (
                    <div className="text-xs text-slate-500 italic px-3 py-2">
                        {t("card_fleet.bulk.no_employees_available")}
                    </div>
                ) : employees.map((e) => (
                    <ChoiceRow
                        key={e.id}
                        value={e.id}
                        label={`${e.name}${e.employee_code ? ` · ${e.employee_code}` : ""}`}
                        selected={pick === e.id}
                        onSelect={setPick}
                        testId={`assign-choice-${e.id}`}
                    />
                ))}
            </div>
            <div className="mt-5">
                <PrimaryButton onClick={() => onChoose({ employee_id: pick || null })} testId="assign-review-changes">
                    {t("card_fleet.bulk.review_changes")}
                </PrimaryButton>
            </div>
        </Shell>
    );
}

// ───────────────────── schedule (template-copy) ─────────────────────
export function ScheduleConfigurator({ open, onClose, onChoose, selectedCardIds }) {
    const { t } = useTranslation();
    const [sources, setSources] = useState([]);
    const [pick, setPick] = useState(""); // card id or "_static_"

    useEffect(() => {
        if (!open) return;
        // Limit candidates to selected cards that have an active schedule.
        Promise.all(selectedCardIds.slice(0, 200).map((cid) =>
            api.get(`/cards/${cid}`).then((r) => r.data).catch(() => null),
        )).then((cards) => {
            const ready = (cards || []).filter((c) => c && c.timing_enabled && (c.timing_schedule || []).length);
            setSources(ready);
            if (ready.length) setPick(ready[0].id);
            else setPick("_static_");
        });
    }, [open, selectedCardIds]);

    if (!open) return null;
    const handleProceed = () => {
        if (pick === "_static_") {
            onChoose({ timing_enabled: false, timing_schedule: [] });
            return;
        }
        const src = sources.find((s) => s.id === pick);
        if (!src) return;
        onChoose({
            timing_enabled: true,
            timing_schedule: src.timing_schedule || [],
        });
    };

    return (
        <Shell title={t("card_fleet.bulk.action_schedule")} onClose={onClose} testId="bulk-config-schedule">
            <p className="text-xs text-slate-400 mb-3">{t("card_fleet.bulk.schedule_template_hint")}</p>
            {sources.length === 0 ? (
                <div className="rounded-lg border px-3 py-3 text-xs text-slate-400"
                     style={{ borderColor: "rgba(148,163,184,0.20)" }}>
                    {t("card_fleet.bulk.schedule_template_disabled")}
                </div>
            ) : (
                <>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                        {t("card_fleet.bulk.schedule_template_source")}
                    </div>
                    <div className="space-y-2 max-h-[36vh] overflow-y-auto pr-1">
                        {sources.map((s) => (
                            <ChoiceRow
                                key={s.id}
                                value={s.id}
                                label={`#${s.card_id || s.name || s.id} · ${(s.timing_schedule || []).length} block(s)`}
                                selected={pick === s.id}
                                onSelect={setPick}
                                testId={`schedule-source-${s.id}`}
                            />
                        ))}
                    </div>
                </>
            )}
            <div className="mt-3">
                <ChoiceRow
                    value="_static_"
                    label={t("card_fleet.bulk.schedule_set_static_label")}
                    selected={pick === "_static_"}
                    onSelect={setPick}
                    testId="schedule-source-static"
                />
            </div>
            <div className="mt-5">
                <PrimaryButton onClick={handleProceed} disabled={!pick} testId="schedule-review-changes">
                    {t("card_fleet.bulk.review_changes")}
                </PrimaryButton>
            </div>
        </Shell>
    );
}
